// src/pages/dashboard/DashboardWarek.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle,
  CheckSquare,
  ChevronRight,
  ExternalLink,
  Gavel,
  HelpCircle,
  Hourglass,
  Info,
  Search,
  Target,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { Badge, Breadcrumb, Button, Card, Input, Loading, Modal, ProgressBar, Select } from '../../components/ui';
import api from '../../services/api';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useAuthStore } from '../../store/authStore';
import { ResolvedLocalItem, TingkatRisiko } from '../../types';
import { formatDate } from '../../utils/formatDate';

const STATUS_COLORS = ['#F59E0B', '#8B5CF6', '#D97706', '#3B82F6', '#22C55E', '#0F766E'];

interface KpiData {
  menunggu_validasi: number;
  total_laporan_bidang: number;
  laporan_siap_rapim: number;
  jadwal_rapat: number;
  selesai: number;
  selesai_lokal: number;
  overdue: number;
  menunggu_konfirmasi: number;
  progress_ppepp: number;
  rata_rata_penyelesaian: number;
}

interface StatusItem {
  status: string;
  jumlah: number;
  helper: string;
}

interface RapatTerdekatItem {
  id: number;
  judul: string;
  tanggal: string | null;
  tempat: string | null;
  status: string;
  jumlah_peserta?: number;
  jumlah_laporan?: number;
}

interface PendingItem {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  risiko?: string;
  unit?: { id: number; name: string };
  user?: { id: number; name: string };
  kategori_masalah?: { id: number; nama: string };
  created_at?: string;
}

interface FakultasItem {
  unit_id?: number;
  unit?: string;
  name?: string;
  total: number;
  selesai: number;
  overdue: number;
  completion_rate?: number;
}

interface AuditReport {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  resolution_level?: string | null;
  unit?: { name?: string | null } | null;
  user?: { name?: string | null } | null;
  target_efektif?: string | null;
  target_selesai?: string | null;
  created_at?: string | null;
}

interface AuditMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface Periode {
  id: number;
  nama: string;
  is_active?: boolean;
}

const toRiskLevel = (risk?: string): TingkatRisiko => {
  const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
  return ['rendah', 'sedang', 'tinggi', 'kritis'].includes(normalized) ? normalized : 'sedang';
};

const DashboardWarek: React.FC = () => {
  const caps = useCapabilities();
  const { user } = useAuthStore();
  const warekRole = user?.role?.display_name || 'Wakil Rektor';

  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<KpiData>({
    menunggu_validasi: 0,
    total_laporan_bidang: 0,
    laporan_siap_rapim: 0,
    jadwal_rapat: 0,
    selesai: 0,
    selesai_lokal: 0,
    overdue: 0,
    menunggu_konfirmasi: 0,
    progress_ppepp: 0,
    rata_rata_penyelesaian: 0,
  });
  const [pendingValidasi, setPendingValidasi] = useState<PendingItem[]>([]);
  const [jadwalRapat, setJadwalRapat] = useState<RapatTerdekatItem[]>([]);
  const [statusData, setStatusData] = useState<StatusItem[]>([]);
  const [fakultasData, setFakultasData] = useState<FakultasItem[]>([]);
  const [resolvedLocalList, setResolvedLocalList] = useState<ResolvedLocalItem[]>([]);
  const [scopeBidang, setScopeBidang] = useState<string[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditModalTitle, setAuditModalTitle] = useState('Semua Laporan Bidang');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReports, setAuditReports] = useState<AuditReport[]>([]);
  const [auditMeta, setAuditMeta] = useState<AuditMeta | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearchDraft, setAuditSearchDraft] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState('');

  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((item: Periode) => item.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };

    fetchPeriode();
  }, []);

  useEffect(() => {
    if (!isPeriodeReady) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const params: Record<string, string> = {};
        if (selectedPeriode) {
          params.periode_id = selectedPeriode;
        }

        const [dashboardRes, validasiRes, fakultasRes] = await Promise.all([
          api.get('/dashboard', { params }),
          api.get('/validasi/level2', { params: { per_page: 5, ...params } }).catch(() => ({
            data: { data: [], meta: { total: 0 } },
          })),
          api.get('/monitoring/unit-performance', { params }).catch(() => ({
            data: { data: [] },
          })),
        ]);

        const dashboardData = dashboardRes.data?.data || dashboardRes.data || {};
        const laporanPerStatus = dashboardData.laporan_per_status || {};
        const kategoriScope = Array.isArray(dashboardData.kategori_scope)
          ? dashboardData.kategori_scope.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          : [];

        const rawValidasi = validasiRes.data?.data || validasiRes.data || {};
        const validasiList = Array.isArray(rawValidasi) ? rawValidasi : (rawValidasi.data || []);
        const validasiTotal = Number(rawValidasi?.meta?.total || rawValidasi?.total || validasiList.length || 0);

        const rapatTerjadwal = (Array.isArray(dashboardData.rapim_terdekat) ? dashboardData.rapim_terdekat : [])
          .filter((item: RapatTerdekatItem) =>
            ['dijadwalkan', 'berlangsung'].includes(String(item.status || '').toLowerCase())
          )
          .sort((left: RapatTerdekatItem, right: RapatTerdekatItem) => {
            const leftTime = left.tanggal ? new Date(left.tanggal).getTime() : Number.MAX_SAFE_INTEGER;
            const rightTime = right.tanggal ? new Date(right.tanggal).getTime() : Number.MAX_SAFE_INTEGER;
            return leftTime - rightTime;
          });

        setPendingValidasi(validasiList.slice(0, 5));
        setJadwalRapat(rapatTerjadwal);
        setResolvedLocalList(Array.isArray(dashboardData.resolved_local_list) ? dashboardData.resolved_local_list : []);
        setScopeBidang(kategoriScope);

        setKpiData({
          menunggu_validasi: validasiTotal,
          total_laporan_bidang: Number(dashboardData.total_laporan || 0),
          laporan_siap_rapim: Number(laporanPerStatus.antrean_rapim || 0),
          jadwal_rapat: rapatTerjadwal.length,
          selesai: Number(laporanPerStatus.selesai || 0) + Number(laporanPerStatus.diarsipkan || 0),
          selesai_lokal: Number(dashboardData.resolved_local_count || 0),
          overdue: Number(dashboardData.laporan_overdue || 0),
          menunggu_konfirmasi: Number(laporanPerStatus.menunggu_konfirmasi || 0),
          progress_ppepp: Number(dashboardData.progress_ppepp || 0),
          rata_rata_penyelesaian: Number(dashboardData.rata_rata_penyelesaian || 0),
        });

        setStatusData([
          { status: 'Butuh Persetujuan WR', jumlah: validasiTotal, helper: 'Laporan dari Dekan atau Kepala Lembaga yang menunggu keputusan Anda' },
          { status: 'Antrean RAPIM', jumlah: Number(laporanPerStatus.antrean_rapim || 0), helper: 'Laporan telah disetujui dan menunggu pembahasan dalam Rapat Pimpinan.' },
          { status: 'Sedang Dikerjakan PIC', jumlah: Number(laporanPerStatus.berlangsung || 0), helper: 'Sudah punya keputusan dan sedang dikerjakan oleh PIC' },
          { status: 'Menunggu Konfirmasi PIC', jumlah: Number(laporanPerStatus.menunggu_konfirmasi || 0), helper: 'PIC menyatakan selesai 100% dan menunggu pengesahan pihak yang memiliki kewenangan konfirmasi penyelesaian' },
          { status: 'Tuntas via RAPIM', jumlah: Number(laporanPerStatus.selesai || 0) + Number(laporanPerStatus.diarsipkan || 0), helper: 'Selesai setelah keputusan rapat pimpinan' },
          { status: 'Ditangani Langsung', jumlah: Number(dashboardData.resolved_local_count || 0), helper: 'Selesai di level Dekan, Kepala Lembaga, atau Warek tanpa RAPIM' },
        ].filter((item) => item.jumlah > 0));

        const fakultasPayload = fakultasRes.data?.data || fakultasRes.data || [];
        if (Array.isArray(fakultasPayload)) {
          const normalizedUnits = fakultasPayload
            .map((item: any) => ({
              unit_id: item.unit_id,
              unit: item.unit || item.name || '-',
              total: Number(item.total || 0),
              selesai: Number(item.selesai || 0),
              overdue: Number(item.overdue || 0),
              completion_rate: item.total > 0 ? Math.round((Number(item.selesai || 0) / Number(item.total || 0)) * 100) : 0,
            }))
            .sort((left: FakultasItem, right: FakultasItem) => (right.completion_rate || 0) - (left.completion_rate || 0));

          setFakultasData(normalizedUnits);
        } else {
          setFakultasData([]);
        }
      } catch (error) {
        console.error('Failed to load Dashboard Warek:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isPeriodeReady, selectedPeriode]);

  useEffect(() => {
    if (!showAuditModal) return;

    let cancelled = false;

    const fetchAuditReports = async () => {
      try {
        setAuditLoading(true);

        const params: Record<string, any> = {
          page: auditPage,
          per_page: 8,
        };

        if (selectedPeriode) params.periode_id = selectedPeriode;
        if (auditStatusFilter) params.status = auditStatusFilter;
        if (auditSearch.trim()) params.search = auditSearch.trim();

        const res = await api.get('/laporan', { params });
        if (cancelled) return;

        const payload = res.data || {};
        const items = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.data?.data)
            ? payload.data.data
            : [];
        const meta = payload.meta || payload.data?.meta || null;

        setAuditReports(items);
        setAuditMeta(meta);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load vice rector audit reports:', error);
          setAuditReports([]);
          setAuditMeta(null);
        }
      } finally {
        if (!cancelled) {
          setAuditLoading(false);
        }
      }
    };

    fetchAuditReports();

    return () => {
      cancelled = true;
    };
  }, [showAuditModal, auditPage, auditSearch, auditStatusFilter, selectedPeriode]);

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '-';
    return new Date(value).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysAgo = (value?: string | null): string => {
    if (!value) return '-';
    const days = Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Hari ini';
    if (days === 1) return 'Kemarin';
    return `${days} hari lalu`;
  };

  if (loading) {
    return <Loading variant="page" message="Memuat dashboard Warek..." />;
  }

  const periodeOptions = [
    { value: '', label: 'Semua Periode' },
    ...periodeList.map((item) => ({
      value: String(item.id),
      label: `${item.nama}${item.is_active ? ' (Aktif)' : ''}`,
    })),
  ];

  const selectedPeriodeLabel = periodeOptions.find((item) => item.value === selectedPeriode)?.label || 'Semua Periode';

  const auditStatusOptions = [
    { value: '', label: 'Semua Status' },
    { value: 'diajukan', label: 'Diajukan' },
    { value: 'validasi_l1', label: 'Persetujuan Dekan/Kepala Lembaga' },
    { value: 'validasi_l2', label: 'Persetujuan Wakil Rektor' },
    { value: 'antrean_rapim', label: 'Menunggu RAPIM' },
    { value: 'diputuskan', label: 'Sudah Diputuskan' },
    { value: 'berlangsung', label: 'Sedang Dikerjakan' },
    { value: 'menunggu_konfirmasi', label: 'Menunggu Konfirmasi' },
    { value: 'selesai', label: 'Selesai' },
    { value: 'diarsipkan', label: 'Diarsipkan' },
    { value: 'diselesaikan_lokal', label: 'Ditangani Langsung' },
  ];

  const openAuditModal = (status = '', title = 'Semua Laporan Bidang') => {
    setAuditModalTitle(title);
    setAuditPage(1);
    setAuditSearchDraft('');
    setAuditSearch('');
    setAuditStatusFilter(status);
    setShowAuditModal(true);
  };

  const totalUnit = fakultasData.length;
  const avgCompletionRate = totalUnit > 0
    ? Math.round(fakultasData.reduce((sum, item) => sum + (item.completion_rate || 0), 0) / totalUnit)
    : 0;

  const totalTuntas = kpiData.selesai + kpiData.selesai_lokal;
  const hasMoreResolvedLocal = kpiData.selesai_lokal > 4;
  const jadwalRapatSubtitle = kpiData.jadwal_rapat > 0
    ? 'Rapat yang sudah ditetapkan sekretaris'
  : kpiData.laporan_siap_rapim > 0
      ? `${kpiData.laporan_siap_rapim} laporan dalam antrean RAPIM`
      : 'Belum ada rapat yang dijadwalkan';
  const rapimReadyMessage = kpiData.jadwal_rapat > 0
    ? 'Laporan telah disetujui dan siap dibahas pada agenda Rapat Pimpinan.'
    : 'Laporan telah disetujui, menunggu penetapan jadwal Rapat Pimpinan dari sekretaris.';

  const kpiCards = [
    {
      title: 'Butuh Persetujuan WR',
      value: kpiData.menunggu_validasi,
      subtitle: 'Menunggu keputusan Anda',
      icon: <CheckSquare className="w-5 h-5" />,
      color: 'text-warning',
      bg: 'bg-warning/10',
      link: caps.canValidate ? '/validasi/level2' : undefined,
    },
    {
      title: 'Jadwal Rapat',
      value: kpiData.jadwal_rapat,
      subtitle: jadwalRapatSubtitle,
      icon: <Calendar className="w-5 h-5" />,
      color: 'text-purple-600',
      bg: 'bg-purple-100',
      link: caps.canManageRapim ? '/rapim' : undefined,
    },
    {
      title: 'Menunggu Konfirmasi PIC',
      value: kpiData.menunggu_konfirmasi,
      subtitle: 'PIC sudah selesai 100%',
      icon: <Hourglass className="w-5 h-5" />,
      color: 'text-amber-600',
      bg: 'bg-amber-100',
    },
    {
      title: 'Terlambat',
      value: kpiData.overdue,
      subtitle: 'Lewat target waktu',
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'text-danger',
      bg: 'bg-danger/10',
      link: '/laporan?status=overdue',
    },
    {
      title: 'Tuntas via RAPIM',
      value: kpiData.selesai,
      subtitle: 'Selesai setelah rapat',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'text-success',
      bg: 'bg-success/10',
    },
    {
      title: 'Ditangani Langsung',
      value: kpiData.selesai_lokal,
      subtitle: 'Selesai tanpa RAPIM',
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'text-teal-main',
      bg: 'bg-teal-main/10',
      onClick: () => openAuditModal('diselesaikan_lokal', 'Laporan Ditangani Langsung'),
    },
    {
      title: 'Progres Bidang',
      value: `${kpiData.progress_ppepp}%`,
      subtitle: 'Tingkat ketuntasan bidang',
      icon: <Target className="w-5 h-5" />,
      color: 'text-info',
      bg: 'bg-info/10',
    },
  ];

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: warekRole }]} />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard {warekRole}
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <Building2 className="w-4 h-4 inline mr-1" />
            Bidang:{' '}
            <span className="font-semibold text-teal-main">
              {scopeBidang.length > 0 ? scopeBidang.join(', ') : 'Semua kategori'}
            </span>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openAuditModal('', 'Semua Laporan Bidang')}
          >
            Semua Laporan
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => setShowGuide((prev) => !prev)}
          >
            Panduan
          </Button>
          <Select
            options={periodeOptions}
            value={selectedPeriode}
            onChange={setSelectedPeriode}
            className="w-full sm:w-60"
          />
        </div>
      </div>

      {showGuide && (
        <Card className="border border-teal-main/10 bg-gradient-to-r from-white via-white to-teal-main/5 animate-in">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-main/10 shrink-0">
                <Info className="w-5 h-5 text-teal-main" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-dark">Cara membaca dashboard Warek</h2>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Ringkasan singkat agar istilah dan angka di dashboard lebih mudah dibaca oleh Warek.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="text-neutral-text-secondary hover:text-teal-main transition-colors shrink-0"
              aria-label="Tutup panduan dashboard Warek"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Jadwal Rapat</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan rapat yang benar-benar sudah dijadwalkan sekretaris. Jika nilainya 0 tetapi ada laporan siap RAPIM, berarti jadwalnya belum ditetapkan.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Menunggu Konfirmasi PIC</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menunjukkan laporan yang sudah diputuskan untuk ditindaklanjuti, progres pelaksanaannya telah mencapai 100%, dan kini menunggu pengesahan akhir dari pihak yang memiliki kewenangan konfirmasi penyelesaian, yaitu Admin, Rektor, atau Wakil Rektor.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Tuntas via RAPIM</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Mencakup laporan yang telah dibahas dalam Rapat Pimpinan, diputuskan tindak lanjutnya, dikerjakan oleh PIC, lalu dinyatakan selesai atau diarsipkan setelah prosesnya tuntas.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Ditangani Langsung di Bidang Anda</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan laporan yang diselesaikan langsung pada level pimpinan unit yang berwenang, yaitu Dekan, Kepala Lembaga, atau Warek sesuai bidangnya, tanpa perlu dibawa ke RAPIM.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Progres Bidang</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Angka ini menunjukkan seberapa banyak laporan di bidang Anda yang sudah tuntas. Perhitungannya mencakup laporan yang selesai melalui RAPIM, laporan yang sudah diarsipkan, dan laporan yang diselesaikan langsung oleh pimpinan unit yang berwenang.
              </p>
            </div>
          </div>
        </Card>
      )}

      {(kpiData.menunggu_validasi > 0 || kpiData.overdue > 0 || kpiData.laporan_siap_rapim > 0 || kpiData.menunggu_konfirmasi > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {kpiData.menunggu_validasi > 0 && caps.canValidate && (
            <div className="p-3 bg-warning/5 border border-warning/20 rounded-xl flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-warning shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-warning">
                  {kpiData.menunggu_validasi} laporan menunggu persetujuan Warek
                </p>
              </div>
              <Link to="/validasi/level2" className="text-xs font-semibold text-warning hover:underline shrink-0">
                Buka
              </Link>
            </div>
          )}

          {kpiData.laporan_siap_rapim > 0 && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-3">
              <Gavel className="w-5 h-5 text-purple-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-700">
                  {kpiData.laporan_siap_rapim} laporan berada dalam antrean RAPIM
                </p>
                <p className="text-xs text-purple-600 mt-0.5">
                  {rapimReadyMessage}
                </p>
              </div>
              {caps.canManageRapim && (
                <Link to="/rapim" className="text-xs font-semibold text-purple-700 hover:underline shrink-0">
                  Buka RAPIM
                </Link>
              )}
            </div>
          )}

          {kpiData.menunggu_konfirmasi > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-700">
                  {kpiData.menunggu_konfirmasi} laporan menunggu konfirmasi penyelesaian PIC
                </p>
              </div>
            </div>
          )}

          {kpiData.overdue > 0 && (
            <div className="p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-danger shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-danger">
                  {kpiData.overdue} laporan melewati target waktu bidang Anda
                </p>
              </div>
              <Link to="/laporan?status=overdue" className="text-xs font-semibold text-danger hover:underline shrink-0">
                Lihat
              </Link>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
        {kpiCards.map((kpi) => {
          const content = (
            <Card hover={Boolean(kpi.link)} className="h-full">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    {kpi.title}
                  </p>
                  <p className={`text-2xl font-black mt-1 ${kpi.color}`}>{kpi.value}</p>
                  <p className="text-xs text-neutral-text-secondary mt-2 leading-relaxed">
                    {kpi.subtitle}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                  {kpi.icon}
                </div>
              </div>
            </Card>
          );

          return kpi.link ? (
            <Link key={kpi.title} to={kpi.link}>
              {content}
            </Link>
          ) : kpi.onClick ? (
            <button key={kpi.title} type="button" className="w-full text-left" onClick={kpi.onClick}>
              {content}
            </button>
          ) : (
            <div key={kpi.title}>
              {content}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-warning" />
                Butuh Persetujuan Warek
              </h3>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Laporan yang sudah disetujui Dekan atau Kepala Lembaga dan menunggu keputusan Anda
              </p>
            </div>
            {caps.canValidate && (
              <Link to="/validasi/level2" className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1">
                Lihat Semua <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </div>

          <div className="space-y-2">
            {pendingValidasi.length > 0 ? (
              pendingValidasi.map((laporan) => (
                <Link
                  key={laporan.id}
                  to={`/laporan/${laporan.id}`}
                  state={{ fromValidation: true, level: 2 }}
                  className="flex items-center gap-3 p-3 rounded-xl bg-neutral-bg/50 hover:bg-warning/5 border border-transparent hover:border-warning/30 transition-all group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-teal-main">{laporan.kode}</span>
                      <Badge variant="risk" risk={toRiskLevel(laporan.tingkat_risiko || laporan.risiko)} />
                      {laporan.kategori_masalah?.nama && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-neutral-text-secondary">
                          {laporan.kategori_masalah.nama}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium truncate">{laporan.judul}</p>
                    <p className="text-xs text-neutral-text-secondary">
                      {laporan.unit?.name || '-'} | {laporan.user?.name || '-'}
                    </p>
                  </div>
                  <span className="px-3 py-1.5 text-xs font-semibold text-white bg-warning rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                    Buka
                  </span>
                </Link>
              ))
            ) : (
              <div className="text-center py-10">
                <CheckSquare className="w-10 h-10 mx-auto mb-2 text-success opacity-50" />
                <p className="text-sm font-medium text-neutral-text-primary">
                  Tidak ada laporan menunggu persetujuan Warek
                </p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Jika ada laporan baru dari bidang Anda, laporan akan muncul di sini.
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-teal-dark">Sebaran Status Laporan</h3>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Ringkasan alur laporan di bidang yang berada di bawah pengawasan Anda
              </p>
            </div>
          </div>

          {statusData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="48%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="jumlah"
                    nameKey="status"
                  >
                    {statusData.map((_, index) => (
                      <Cell key={index} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, _name, item: any) => [`${value} laporan`, item?.payload?.helper || '']}
                  />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value: string) => (
                      <span className="text-xs text-neutral-text-secondary">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="pt-3 border-t border-neutral-border space-y-2">
                {statusData.slice(0, 3).map((item) => (
                  <div key={item.status} className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-neutral-text-primary">{item.status}</p>
                      <p className="text-[10px] text-neutral-text-secondary">{item.helper}</p>
                    </div>
                    <span className="text-sm font-black text-teal-main">{item.jumlah}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data status untuk bidang ini
            </div>
          )}
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Jadwal Rapat Pimpinan Terdekat
            </h3>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Rapat yang sudah dijadwalkan sekretaris. Warek dapat memakai blok ini untuk memastikan kapan pembahasan dilakukan.
            </p>
          </div>
          {caps.canManageRapim && (
            <Link
              to="/rapim"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors shrink-0"
            >
              Buka RAPIM
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        {jadwalRapat.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {jadwalRapat.slice(0, 3).map((rapat) => (
              <Link
                key={rapat.id}
                to={`/rapim/${rapat.id}`}
                className="p-4 rounded-xl border border-purple-100 bg-purple-50/30 hover:bg-purple-50 hover:border-purple-200 transition-all group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-neutral-text-primary truncate group-hover:text-purple-700 transition-colors">
                      {rapat.judul}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-1">
                      {formatDateTime(rapat.tanggal)}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-1">
                      Tempat: {rapat.tempat || '-'}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                    String(rapat.status).toLowerCase() === 'berlangsung'
                      ? 'bg-success/10 text-success'
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {String(rapat.status).toLowerCase() === 'berlangsung' ? 'Berlangsung' : 'Dijadwalkan'}
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="text-[10px] px-2 py-1 bg-white rounded-lg text-neutral-text-secondary border border-purple-100">
                    {rapat.jumlah_laporan ?? 0} laporan
                  </span>
                  <span className="text-[10px] px-2 py-1 bg-white rounded-lg text-neutral-text-secondary border border-purple-100">
                    {rapat.jumlah_peserta ?? 0} peserta
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 bg-neutral-bg/30 rounded-xl border border-dashed border-neutral-border">
            <p className="text-sm font-semibold text-neutral-text-primary">
              Belum ada jadwal RAPIM yang ditetapkan
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              {kpiData.laporan_siap_rapim > 0
                ? `${kpiData.laporan_siap_rapim} laporan sudah siap dibahas, tetapi jadwal RAPIM-nya belum ditetapkan sekretaris.`
                : 'Ketika sekretaris menjadwalkan rapat, detailnya akan muncul di blok ini.'}
            </p>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-main" />
              Laporan Diselesaikan Langsung di Bidang Anda
            </h3>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Ringkasan laporan yang selesai tanpa RAPIM pada bidang Anda.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-success/10 text-success">
              {kpiData.selesai_lokal} laporan
            </span>
            {resolvedLocalList.length > 0 && (
              <button
                type="button"
                onClick={() => openAuditModal('diselesaikan_lokal', 'Laporan Ditangani Langsung')}
                className="text-xs font-semibold text-teal-main hover:underline shrink-0"
              >
                Lihat Semua
              </button>
            )}
          </div>
        </div>

        {hasMoreResolvedLocal && (
          <div className="mb-4 p-3 rounded-xl bg-neutral-bg/50 border border-neutral-border">
            <p className="text-xs text-neutral-text-secondary">
              Yang tampil di dashboard adalah ringkasan terbaru. Untuk melihat daftar lengkapnya, buka <span className="font-semibold text-teal-main">Lihat Semua</span>.
            </p>
          </div>
        )}

        {resolvedLocalList.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {resolvedLocalList.slice(0, 4).map((item) => (
              <Link
                key={item.id}
                to={`/laporan/${item.id}`}
                className="p-4 rounded-xl border border-success/20 bg-success/5 hover:bg-success/10 transition-all group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-teal-main">{item.kode}</span>
                      <Badge variant="risk" risk={toRiskLevel(item.tingkat_risiko)} />
                    </div>
                    <p className="text-sm font-semibold text-neutral-text-primary truncate group-hover:text-success transition-colors">
                      {item.judul}
                    </p>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-white text-success border border-success/20 shrink-0">
                    {item.resolution_level || 'lokal'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-neutral-text-secondary">
                  <div>
                    <p className="font-semibold text-neutral-text-primary">Unit</p>
                    <p className="mt-0.5">{item.unit || '-'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-text-primary">Bidang</p>
                    <p className="mt-0.5">{item.kategori || '-'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-text-primary">Diselesaikan oleh</p>
                    <p className="mt-0.5">{item.resolved_by || '-'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-text-primary">Waktu</p>
                    <p className="mt-0.5">{getDaysAgo(item.resolved_at)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 bg-neutral-bg/30 rounded-xl border border-dashed border-neutral-border">
            <p className="text-sm font-semibold text-neutral-text-primary">
              Belum ada laporan yang ditangani langsung di bidang Anda
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Ketika Dekan, Kepala Lembaga, atau Warek menyelesaikan laporan tanpa RAPIM, ringkasannya akan tampil di sini.
            </p>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-main/10 rounded-lg">
              <Users className="w-5 h-5 text-teal-main" />
            </div>
            <div>
              <h3 className="text-base font-bold text-teal-dark">Kinerja Unit di Bawah Bidang Anda</h3>
              <p className="text-xs text-neutral-text-secondary">
                {totalUnit} unit | rata-rata penyelesaian {avgCompletionRate}%
              </p>
            </div>
          </div>
        </div>

        {fakultasData.length > 0 ? (
          <>
            <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-4 py-2 bg-neutral-bg rounded-lg text-[10px] font-semibold uppercase tracking-wider text-neutral-text-secondary mb-2">
              <div className="col-span-4">Fakultas / Unit</div>
              <div className="col-span-4">Progress</div>
              <div className="col-span-1 text-center">Total</div>
              <div className="col-span-1 text-center">Tuntas</div>
              <div className="col-span-1 text-center">Terlambat</div>
              <div className="col-span-1 text-center">Rate</div>
            </div>

            <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
              {fakultasData.map((item, index) => {
                const rate = item.completion_rate || 0;
                const colorClass = rate >= 80 ? 'success' : rate >= 50 ? 'warning' : 'danger';

                return (
                  <div key={item.unit_id || index} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 p-3 rounded-lg border border-neutral-border">
                    <div className="sm:col-span-4 flex items-center">
                      <span className="text-sm font-medium text-neutral-text-primary truncate">{item.unit}</span>
                    </div>
                    <div className="sm:col-span-4 flex items-center">
                      <div className="w-full">
                        <ProgressBar value={rate} color={colorClass} size="sm" showLabel={false} />
                      </div>
                    </div>
                    <div className="sm:col-span-1 flex items-center justify-center font-semibold text-sm">{item.total}</div>
                    <div className="sm:col-span-1 flex items-center justify-center font-semibold text-sm text-success">{item.selesai}</div>
                    <div className="sm:col-span-1 flex items-center justify-center font-semibold text-sm text-danger">{item.overdue}</div>
                    <div className="sm:col-span-1 flex items-center justify-center font-black text-sm">{rate}%</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm font-semibold text-neutral-text-primary">Belum ada data kinerja unit</p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Data akan muncul setelah laporan pada bidang Anda mulai diproses.
            </p>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Ringkasan Performa Bidang
        </h3>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 bg-teal-main/5 rounded-xl text-center">
            <p className="text-3xl font-black text-teal-main">{kpiData.total_laporan_bidang}</p>
            <p className="text-xs text-neutral-text-secondary mt-1">Total Laporan Bidang</p>
          </div>
          <div className="p-4 bg-success/5 rounded-xl text-center">
            <p className="text-3xl font-black text-success">
              {kpiData.total_laporan_bidang > 0
                ? Math.round((totalTuntas / kpiData.total_laporan_bidang) * 100)
                : 0}%
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">Tingkat Ketuntasan</p>
          </div>
          <div className="p-4 bg-info/5 rounded-xl text-center">
            <p className="text-3xl font-black text-info">{kpiData.rata_rata_penyelesaian}</p>
            <p className="text-xs text-neutral-text-secondary mt-1">Rata-rata Hari</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-xl text-center">
            <p className="text-3xl font-black text-purple-600">{kpiData.progress_ppepp}%</p>
            <p className="text-xs text-neutral-text-secondary mt-1">Progress Tindak Lanjut</p>
          </div>
        </div>
      </Card>

      <Modal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        title={auditModalTitle}
        size="2xl"
        footer={(
          <Button variant="ghost" onClick={() => setShowAuditModal(false)}>
            Tutup
          </Button>
        )}
      >
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-dark">
                Laporan bidang untuk periode {selectedPeriodeLabel}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Anda bisa melihat semua laporan pada periode ini. Gunakan pencarian atau pilih status untuk menemukan laporan yang ingin dilihat.
              </p>
            </div>
            <Badge className="self-start" size="sm">
              {auditMeta?.total ?? 0} laporan
            </Badge>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Total Bidang
              </p>
              <p className="text-xl font-black text-teal-main mt-1">{kpiData.total_laporan_bidang}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Butuh Persetujuan
              </p>
              <p className="text-xl font-black text-warning mt-1">{kpiData.menunggu_validasi}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Tuntas via RAPIM
              </p>
              <p className="text-xl font-black text-success mt-1">{kpiData.selesai}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Ditangani Langsung
              </p>
              <p className="text-xl font-black text-teal-main mt-1">{kpiData.selesai_lokal}</p>
            </div>
          </div>

          <form
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px_auto_auto] gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setAuditPage(1);
              setAuditSearch(auditSearchDraft.trim());
            }}
          >
            <Input
              placeholder="Cari kode, judul, atau unit"
              value={auditSearchDraft}
              onChange={(event) => setAuditSearchDraft(event.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
            <Select
              options={auditStatusOptions}
              value={auditStatusFilter}
              onChange={(value) => {
                setAuditPage(1);
                setAuditStatusFilter(value);
              }}
            />
            <Button type="submit" variant="outline">
              Cari
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAuditPage(1);
                setAuditSearchDraft('');
                setAuditSearch('');
                setAuditStatusFilter('');
              }}
            >
              Reset
            </Button>
          </form>

          <div className="rounded-2xl border border-neutral-border overflow-hidden">
            <div className="px-4 py-3 bg-neutral-bg border-b border-neutral-border flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-text-primary">Daftar Laporan</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Data di sini menyesuaikan periode yang sedang dipilih.
                </p>
              </div>
              {auditLoading && (
                <span className="text-xs text-neutral-text-secondary">Memuat data...</span>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-border">
              {auditLoading ? (
                <div className="py-12">
                  <Loading message="Memuat laporan..." />
                </div>
              ) : auditReports.length > 0 ? (
                auditReports.map((report) => (
                  <div key={report.id} className="p-4 bg-white hover:bg-neutral-bg/60 transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs font-mono font-semibold text-teal-main">{report.kode}</span>
                          <Badge variant="status" status={report.status} size="xs" useShortLabel />
                          {report.tingkat_risiko && (
                            <Badge variant="risk" risk={toRiskLevel(report.tingkat_risiko)} size="xs" />
                          )}
                        </div>
                        <p className="text-sm font-bold text-neutral-text-primary">{report.judul}</p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-text-secondary">
                          <span>Pelapor: {report.user?.name || '-'}</span>
                          <span>Unit: {report.unit?.name || '-'}</span>
                          <span>Target: {formatDate(report.target_efektif || report.target_selesai || '-')}</span>
                          <span>Dibuat: {formatDate(report.created_at || '-')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link to={`/laporan/${report.id}`}>
                          <Button variant="outline" size="sm">
                            Lihat Detail
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center">
                  <Users className="w-10 h-10 mx-auto text-neutral-text-secondary mb-3" />
                  <p className="text-sm font-semibold text-neutral-text-primary">
                    Belum ada laporan yang cocok
                  </p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Coba pencarian lain atau pilih status yang berbeda.
                  </p>
                </div>
              )}
            </div>
          </div>

          {auditMeta && auditMeta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-text-secondary">
                Halaman {auditMeta.current_page} dari {auditMeta.last_page}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page <= 1}
                  onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                >
                  Sebelumnya
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page >= auditMeta.last_page}
                  onClick={() => setAuditPage((prev) => (
                    auditMeta ? Math.min(auditMeta.last_page, prev + 1) : prev + 1
                  ))}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DashboardWarek;
